import { useTranslation } from 'react-i18next';
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import MainMenu from './MainMenu';
import PeriodicTable from './PeriodicTable';
import MobileModal from '../pages/MobileModal.js';
import ReadElementPage from '../pages/ReadElementPage.js';
import SearchPage from '../pages/SearchPage.js';
import ElementListPage from '../pages/ElementListPage.js';
import { Suspense, useEffect, useLayoutEffect, useState } from 'react';
import { isAndroid, isIOS, isIPad13, isMobile } from 'react-device-detect';
import i18next from 'i18next';
// import Table6 from './Table6';
import { setFontSizeVariable } from '../services/functions';
import { getAuthCookie } from '../hooks/useAuth';
import CookieNotification from './CookieNotification';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { allElementsState, authState } from '../states/atoms';
import Loader from './Loader';
// Редкие страницы (и KaTeX калькуляторов) — в отдельных чанках, не в main.js
import {
  CalculatorModal,
  CalculatorPage,
  ComparePage,
  LanguageSelectPage,
  PrivacyPolicyIosPage,
  PrivacyPolicyPage,
  SettingsPage,
  Table1,
  Table2,
  Table3,
  Table4,
  Table5,
  TablesPage
} from '../pages/lazyPages';
import { FirebaseBrain, setUserContext } from '../services/FirebaseBrain/FirebaseBrain';
import {
  NO_LANG_PATHS,
  applyLanguage,
  buildPath,
  parsePath,
  toLangTag,
  toUrlCode
} from '../services/language';
import { startup } from '../services/startup';
import PageMeta from './PageMeta';
import SeoLinks from './SeoLinks';

const App = () => {
  const isAuthenticated = useRecoilValue(authState);
  const setIsAuthenticated = useSetRecoilState(authState);

  const { t } = useTranslation();
  const setAllElementsData = useSetRecoilState(allElementsState);
  // Если стартовые данные уже пришли (пререндеренная страница ждёт их до первого рендера) — сразу контент
  const [isLoading, setIsLoading] = useState(!startup.isReady);
  const [gasCurrentCalculator, setGasCurrentCalculator] = useState('pressure');
  const [lang, setLang] = useState(startup.lang);


  const options = [
    { val: 'pressure', label: t('calc.translate.ideal_gas_calc_tab0') },
    { val: 'volume', label: t('calc.translate.ideal_gas_calc_tab1') },
    { val: 'amount', label: t('calc.translate.ideal_gas_calc_tab2') },
    { val: 'temperature', label: t('calc.translate.ideal_gas_calc_tab3') }
  ];

  // Язык, check_auth и список элементов загружаются параллельно при старте (services/startup)
  const initializeAuthAndFetchData = async () => {
    await startup.ready;
    if (isLoading) {
      setIsAuthenticated(startup.isAuthenticated);
      setAllElementsData(startup.allElements);
    }

    setUserContext({
      userId: getAuthCookie() || null,
      isSubscribed: startup.isAuthenticated
    });
    // cold/warm определяется автоматически внутри FirebaseBrain по lastExit из localStorage
    FirebaseBrain.logSessionStarted(i18next.language);

    setIsLoading(false);
  };

  // Формула для KaTeX (рисует CalculatorModal)
  const getCurrentGasFormula = () => {
    switch (gasCurrentCalculator) {
      case 'pressure':
        return '\\Large P = \\frac {nRT}{V}';
      case 'volume':
        return '\\Large V = \\frac {nRT}{P}';
      case 'amount':
        return '\\Large n = \\frac {PV}{RT}';
      case 'temperature':
        return '\\Large T = \\frac {PV}{nR}';
      default:
        return '\\Large P = \\frac {nRT}{V}';
    }
  };

  const getGasFormulaValues = () => {
    switch (gasCurrentCalculator) {
      case 'pressure':
        return [t('calc.translate.ideal_gas_calc_value2_title'), t('calc.translate.ideal_gas_calc_value3_title'), t('calc.translate.ideal_gas_calc_value4_title')];
      case 'volume':
        return [t('calc.translate.ideal_gas_calc_value1_title'), t('calc.translate.ideal_gas_calc_value3_title'), t('calc.translate.ideal_gas_calc_value4_title')];
      case 'amount':
        return [t('calc.translate.ideal_gas_calc_value1_title'), t('calc.translate.ideal_gas_calc_value2_title'), t('calc.translate.ideal_gas_calc_value4_title')];
      case 'temperature':
        return [t('calc.translate.ideal_gas_calc_value1_title'), t('calc.translate.ideal_gas_calc_value2_title'), t('calc.translate.ideal_gas_calc_value3_title')];
      default:
        return [t('calc.translate.ideal_gas_calc_value2_title'), t('calc.translate.ideal_gas_calc_value3_title'), t('calc.translate.ideal_gas_calc_value4_title')];
    }
  };

  const getGasUnit = () => {
    switch (gasCurrentCalculator) {
      case 'pressure':
        return t('calc.translate.ideal_gas_result_pressure_unit');
      case 'volume':
        return t('calc.translate.ideal_gas_result_volume_unit');
      case 'amount':
        return t('calc.translate.ideal_gas_result_moles_unit');
      case 'temperature':
        return t('calc.translate.ideal_gas_result_temperature_unit');
      default:
        return t('calc.translate.ideal_gas_result_pressure_unit');
    }
  };

  const getGasLegend = () => {
    switch (gasCurrentCalculator) {
      case 'pressure':
        return t('calc.translate.ideal_gas_result_pressure_answer_variable');
      case 'volume':
        return t('calc.translate.ideal_gas_result_volume_answer_variable');
      case 'amount':
        return t('calc.translate.ideal_gas_result_moles_answer_variable');
      case 'temperature':
        return t('calc.translate.ideal_gas_result_temperature_answer_variable');
      default:
        return t('calc.translate.ideal_gas_result_pressure_answer_variable');
    }
  };

  useLayoutEffect(() => {
    // Классы ставит скрипт в public/index.html по userAgent; здесь — точное определение react-device-detect
    const { classList } = document.documentElement;
    classList.toggle('is-mobile', isMobile || isIPad13);
    classList.toggle('is-android', isAndroid);
    classList.toggle('is-ios', !isAndroid && isIOS);
  }, []);

  useEffect(() => {
    setFontSizeVariable();
    initializeAuthAndFetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Смена языка (LanguageSelectPage) переписывает префикс в URL и пересоздаёт Router с новым basename
    const onLanguageChanged = newLang => {
      const { pathname, search, hash } = window.location;
      if (NO_LANG_PATHS.includes(pathname)) {
        return;
      }
      const { lang: urlLang, rest } = parsePath(pathname);
      if (urlLang !== newLang) {
        window.history.replaceState(null, '', `${buildPath(newLang, rest)}${search}${hash}`);
      }
      setLang(newLang);
    };
    // Назад/вперёд в истории на адрес с другим языком
    const onPopState = () => {
      const { lang: urlLang } = parsePath(window.location.pathname);
      if (urlLang && urlLang !== i18next.language) {
        applyLanguage(urlLang);
      }
    };
    i18next.on('languageChanged', onLanguageChanged);
    window.addEventListener('popstate', onPopState);
    return () => {
      i18next.off('languageChanged', onLanguageChanged);
      window.removeEventListener('popstate', onPopState);
    };
  }, []);

  useEffect(() => {
    // Вешаем слушатели завершения сессии (visibilitychange + pagehide) один раз.
    // Логика дедупликации и сохранения lastExit живёт внутри FirebaseBrain.
    FirebaseBrain.installSessionListeners();
  }, []);

  const renderContent = () => {
    // На телефонах окно со ссылками на приложение показывается поверх контента, а не вместо него:
    // мобильный робот Google (mobile-first indexing) должен видеть страницы.
    // Разметка окна есть у всех (и в пререндере) — видно его только при html.is-mobile, чтобы на телефоне
    // оно появлялось вместе с пререндеренной страницей, а не после загрузки JS
    const showMobileModal = !NO_LANG_PATHS.includes(window.location.pathname);
    const content = !isLoading ? <>
        <Helmet>
          <html lang={toLangTag(lang)} />
          <meta property='og:type' content='website' />
          <meta property='og:site_name' content={t('string.translate.name_ab')} />
        </Helmet>
        {/* Главная страница; страницы поверх неё (элемент, поиск, таблицы) задают свои title/description */}
        <PageMeta
          title={`${t('string.translate.name_ab')} ${new Date().getFullYear()}`}
          description={`${t('string.translate.name_ab')}: ${t('string.translate.dm_left9')}, ${t('string.translate.dm_left8')}, ${t('string.translate.dm_left2')}. ${t('string.translate.other_tables_description')}`}
        />
        <CookieNotification />
        {NO_LANG_PATHS.includes(window.location.pathname) ? (
          <Router>
            <Suspense fallback={null}>
              <Switch>
                <Route
                  path='/privacy-policy'
                  exact
                  render={() => <PrivacyPolicyPage />}
                />
                <Route
                  path='/privacy-policy-ios'
                  exact
                  render={() => <PrivacyPolicyIosPage />}
                />
              </Switch>
            </Suspense>
          </Router>
        ) : (
          <Router basename={`/${toUrlCode(lang)}`} key={lang}>
            <SeoLinks lang={lang} />
            <section className='page-main'>
              <MainMenu />
              <div className='page-main__wrapper'>
                <PeriodicTable />
              </div>
            </section>
            <Suspense fallback={null}>
              <Switch>
                <Route
                  path='/element/:id'
                  exact
                  render={({ match }) => {
                    const { id } = match.params;
                    return <ReadElementPage id={id} />;
                  }}
                />
                {isAuthenticated && <Route
                  path='/element/:id/compare'
                  exact
                  render={({ match }) => {
                    const { id } = match.params;
                    return <ComparePage id={id} />;
                  }}
                />}
                <Route path='/search' render={() => <SearchPage />} />
                <Route path='/element-list/:property?' render={() => <ElementListPage />} />
                <Route path='/tables' exact component={TablesPage} />
                {isAuthenticated && <Route path='/calculators' exact component={CalculatorPage} />}
                {isAuthenticated &&
                  <Route
                    path='/calculators/calculator-1'
                    render={() => (
                      <CalculatorModal
                        helmetTitle={t('calc.translate.mole_calc_list_name')}
                        title={t('calc.translate.mole_calc_name')}
                        formula={'\\Large n = \\frac {m}{M}'}
                        descr1={t('calc.translate.mole_calc_instruction_subTitle')}
                        descr2={t('calc.translate.mole_calc_instruction_bottomTitle')}
                        valuePlaceholder1={t('calc.translate.mole_calc_value0_title')}
                        valuePlaceholder2={t('calc.translate.mole_calc_value1_title')}
                        legend={t('calc.translate.mole_calc_answer_variable')}
                        symbol={'n'}
                        accentColor={'#faa916'}
                      />
                    )}
                  />
                }
                {isAuthenticated &&
                  <Route
                    path='/calculators/calculator-2'
                    render={() => (
                      <CalculatorModal
                        helmetTitle={t('calc.translate.concentrat_calc_list_name')}
                        title={t('calc.translate.concentrat_calc_list_name')}
                        formula={'\\Large C = \\frac {n}{V}'}
                        descr1={t('calc.translate.concentrat_calc_instruction_subTitle')}
                        descr2={t('calc.translate.concentrat_calc_instruction_bottomTitle')}
                        valuePlaceholder1={t('calc.translate.concentrat_calc_value0_title')}
                        valuePlaceholder2={t('calc.translate.concentrat_calc_value1_title')}
                        symbol={'C'}
                        legend={t('calc.translate.concentrat_calc_answer_variable')}
                        unit={t('calc.translate.concentrat_calc_answer_unit')}
                        accentColor={'#006da1'}
                      />
                    )}
                  />
                }
                {isAuthenticated &&
                  <Route
                    path='/calculators/calculator-3'
                    render={() => (
                      <CalculatorModal
                        includeThirdValue
                        switcher={
                          <div className='calculator-page__segmented'>
                            {options.map(({ label, val }) => {
                              return (
                                <div className='calculator-page__segmented-item'>
                                  <input
                                    type='radio'
                                    name={'gasCurrentCalculator'}
                                    value={val}
                                    id={val}
                                    checked={gasCurrentCalculator === val}
                                    onChange={() => setGasCurrentCalculator(val)}
                                  />
                                  <label htmlFor={val}>{label}</label>
                                </div>
                              );
                            })}
                          </div>
                        }
                        helmetTitle={t('calc.translate.deal_gas_calc_list_name')}
                        title={t('calc.translate.ideal_gas_calc_name')}
                        formula={getCurrentGasFormula()}
                        valuePlaceholder1={getGasFormulaValues()[0]}
                        valuePlaceholder2={getGasFormulaValues()[1]}
                        valuePlaceholder3={getGasFormulaValues()[2]}
                        symbol={'C'}
                        legend={getGasLegend()}
                        unit={getGasUnit()}
                        accentColor={'#ce2d4f'}
                        gasCurrentCalculator={gasCurrentCalculator}
                      />
                    )}
                  />
                }
                <Route path='/settings' exact component={SettingsPage} />
                <Route
                  path='/settings/language'
                  exact
                  component={LanguageSelectPage}
                />
                <Route path='/tables/table-1' component={Table1} />
                <Route path='/tables/table-2' component={Table2} />
                <Route path='/tables/table-3' component={Table3} />
                <Route path='/tables/table-4' component={Table4} />
                <Route path='/tables/table-5' component={Table5} />
                {/*<Route path='/tables/table-4' component={Table6} />*/}
              </Switch>
            </Suspense>
          </Router>
        )}
      </> : <Loader />;

    if (!showMobileModal) {
      return content;
    }
    // Контент под окном ограничен размером экрана, иначе широкая десктопная вёрстка раздувает viewport
    return (<>
      <MobileModal />
      <div className='mobile-behind'>{content}</div>
    </>);
  };

  return <>{renderContent()}</>;
};

export default App;